import { useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { format } from 'date-fns'
import { ChevronUp, Loader2, Lock, MessageSquare, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useDiscussion, useNoteInterne, type SujetDiscussion } from '@/api/inbox'
import { useTeamMembers, type TeamMember } from '@/api/agents'
import { useProfile } from '@/api/profile'
import type { DiscussionSummary, TimelineEvent } from '@/types/timeline'

const CLE_ASTUCE = 'callizy.discussion.astuceVue'

function astuceDejaVue(): boolean {
  try {
    return localStorage.getItem(CLE_ASTUCE) === '1'
  } catch {
    return false
  }
}

function retenirAstuce() {
  try {
    localStorage.setItem(CLE_ASTUCE, '1')
  } catch {
    /* stockage indisponible : l'astuce reviendra, rien de grave */
  }
}

const nomMembre = (m: TeamMember) => `${m.user_first_name || ''} ${m.user_last_name || ''}`.trim() || m.user_email
const sansAccents = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

/** « @jo » juste avant le curseur : le début du « @ » et ce qui est tapé après. */
function mentionEnCours(texte: string, curseur: number): { debut: number; requete: string } | null {
  const m = /(^|\s)@([\p{L}\p{N}._-]*)$/u.exec(texte.slice(0, curseur))
  if (!m) return null
  return { debut: curseur - m[2].length - 1, requete: m[2] }
}

const heure = (iso: string | null | undefined) => {
  if (!iso) return ''
  try {
    return format(new Date(iso), 'HH:mm')
  } catch {
    return ''
  }
}

/** Le texte d'une note, les « @Nom » des personnes citées en évidence. */
function TexteAvecMentions({ texte, noms, darkMode }: { texte: string; noms: string[]; darkMode: boolean }) {
  const cibles = noms.filter(Boolean).map((n) => `@${n}`)
  if (!cibles.length) return <>{texte}</>
  const echapper = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const morceaux = texte.split(new RegExp(`(${cibles.map(echapper).join('|')})`, 'g'))
  return (
    <>
      {morceaux.map((m, i) =>
        cibles.includes(m) ? (
          <span key={i} className={cn('font-medium', darkMode ? 'text-indigo-300' : 'text-indigo-600')}>
            {m}
          </span>
        ) : (
          m
        ),
      )}
    </>
  )
}

interface Props {
  phoneNumber: string
  line: string | null
  sujet: SujetDiscussion
  resume?: DiscussionSummary
  /** Plan Team : on peut écrire. La lecture reste ouverte à tous les plans. */
  equipe: boolean
  /** Sous une carte de droite (appel sortant, texto envoyé). */
  aDroite?: boolean
  darkMode: boolean
}

/**
 * The team's discussion on a call or a text (the Allo model): « 2 commentaires
 * · 09:52 » under it, opened in place on a computer. The customer never sees
 * it. The « only your team sees this » tip shows the first time only.
 */
export function DiscussionEquipe({ phoneNumber, line, sujet, resume, equipe, aDroite = false, darkMode }: Props) {
  const { t } = useTranslation()
  const [ouvert, setOuvert] = useState(false)
  const count = resume?.count ?? 0
  if (count === 0 && !equipe) return null

  const lien = cn(
    'inline-flex items-center gap-2 min-h-[32px] pl-3 pr-2 text-[11.5px] border-l-2 transition-colors',
    darkMode ? 'border-slate-700 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-100/70',
  )

  return (
    <div className={cn('flex', aDroite ? 'justify-end' : 'justify-start')}>
      <div className={cn('w-full max-w-[460px] flex flex-col', aDroite ? 'pr-4 items-end' : 'pl-4 items-start')}>
        {!ouvert &&
          (count > 0 ? (
            <button type="button" onClick={() => setOuvert(true)} className={lien}>
              <span className="flex -space-x-1.5">
                {(resume?.authors ?? []).slice(0, 3).map((a) => (
                  <span
                    key={a.user_id}
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded-full border-2 text-[8.5px] font-bold',
                      darkMode
                        ? 'border-slate-950 bg-slate-700 text-slate-100'
                        : 'border-gray-50 bg-indigo-100 text-indigo-800',
                    )}
                  >
                    {a.initials}
                  </span>
                ))}
              </span>
              <span className={cn('font-semibold', darkMode ? 'text-indigo-300' : 'text-indigo-700')}>
                {t('inbox.discussion.comments', { count })}
              </span>
              <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>{heure(resume?.last_at)}</span>
            </button>
          ) : (
            // Nothing yet: a quiet entry point, shown when the card is hovered.
            <button
              type="button"
              onClick={() => setOuvert(true)}
              className={cn(
                lien,
                'opacity-0 group-hover:opacity-100 focus:opacity-100',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              <MessageSquare className="h-3 w-3" />
              {t('inbox.discussion.comment', "Commenter pour l'équipe")}
            </button>
          ))}
        {ouvert && (
          <div className="w-full">
            <Fil
              phoneNumber={phoneNumber}
              line={line}
              sujet={sujet}
              equipe={equipe}
              darkMode={darkMode}
              onFermer={() => setOuvert(false)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function Fil({
  phoneNumber,
  line,
  sujet,
  equipe,
  darkMode,
  onFermer,
}: {
  phoneNumber: string
  line: string | null
  sujet: SujetDiscussion
  equipe: boolean
  darkMode: boolean
  onFermer: () => void
}) {
  const { t } = useTranslation()
  const { data: notes = [], isLoading } = useDiscussion(phoneNumber, sujet)
  const publier = useNoteInterne(phoneNumber, line)
  const { data: profile } = useProfile()
  const { data: membres = [] } = useTeamMembers()
  const [astuce, setAstuce] = useState(!astuceDejaVue())
  const [texte, setTexte] = useState('')
  const [cites, setCites] = useState<Record<number, string>>({})
  const [mention, setMention] = useState<{ debut: number; requete: string } | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)
  const champ = useRef<HTMLTextAreaElement>(null)

  const candidats = useMemo(() => {
    if (!mention) return []
    const q = sansAccents(mention.requete)
    return membres
      .filter((m) => m.user_id !== profile?.id)
      .filter((m) => !q || sansAccents(nomMembre(m)).includes(q) || sansAccents(m.user_email).startsWith(q))
      .slice(0, 6)
  }, [mention, membres, profile?.id])

  const fermerAstuce = () => {
    retenirAstuce()
    setAstuce(false)
  }

  const choisir = (m: TeamMember) => {
    const el = champ.current
    if (!mention || !el) return
    const curseur = el.selectionStart ?? texte.length
    const insere = `@${nomMembre(m)} `
    setTexte(texte.slice(0, mention.debut) + insere + texte.slice(curseur))
    setCites((prev) => ({ ...prev, [m.user_id]: nomMembre(m) }))
    setMention(null)
    requestAnimationFrame(() => {
      const pos = mention.debut + insere.length
      el.focus()
      el.setSelectionRange(pos, pos)
    })
  }

  const surSaisie = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setTexte(e.target.value)
    setMention(mentionEnCours(e.target.value, e.target.selectionStart ?? e.target.value.length))
    setMentionIndex(0)
  }

  const envoyer = () => {
    const contenu = texte.trim()
    if (!contenu || publier.isPending) return
    // Seules les mentions encore présentes dans le texte comptent.
    const mentions = Object.entries(cites)
      .filter(([, nom]) => contenu.includes(`@${nom}`))
      .map(([id]) => Number(id))
    publier.mutate(
      { text: contenu, mentions, sujet },
      {
        onSuccess: () => {
          setTexte('')
          setCites({})
          retenirAstuce()
          setAstuce(false)
        },
        onError: () => toast.error(t('inbox.team.noteError', "La note n'a pas été publiée")),
      },
    )
  }

  const surTouche = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (candidats.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIndex((i) => Math.min(i + 1, candidats.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIndex((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        choisir(candidats[Math.min(mentionIndex, candidats.length - 1)])
        return
      }
      if (e.key === 'Escape') {
        setMention(null)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      envoyer()
    }
  }

  const sourdine = darkMode ? 'text-slate-500' : 'text-slate-400'

  return (
    <div
      className={cn(
        'mt-1 rounded-xl border p-3 space-y-3',
        darkMode ? 'border-slate-700/70 bg-slate-900/70' : 'border-slate-200 bg-white shadow-sm',
      )}
    >
      <div className="flex items-center justify-between">
        <span className={cn('text-[10.5px] font-semibold uppercase tracking-wider', sourdine)}>
          {t('inbox.discussion.title', "Discussion d'équipe")}
        </span>
        <button
          type="button"
          onClick={onFermer}
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] transition-colors',
            darkMode ? 'text-slate-400 hover:bg-white/5' : 'text-slate-500 hover:bg-slate-100',
          )}
        >
          <ChevronUp className="h-3 w-3" />
          {t('inbox.discussion.hide', 'Masquer')}
        </button>
      </div>

      {astuce && (
        <div
          className={cn(
            'flex items-start gap-2 rounded-lg px-3 py-2 text-[12px]',
            darkMode ? 'bg-indigo-500/10 text-indigo-100' : 'bg-indigo-50 text-indigo-950',
          )}
        >
          <Lock className="mt-0.5 h-3 w-3 flex-shrink-0" />
          <span className="flex-1">
            {t('inbox.discussion.tip', 'Seule votre équipe voit cette discussion. Écrivez @ pour prévenir un collègue.')}
          </span>
          <button type="button" onClick={fermerAstuce} className="font-semibold">
            {t('inbox.discussion.ok', 'OK')}
          </button>
        </div>
      )}

      {isLoading ? (
        <Loader2 className={cn('h-4 w-4 animate-spin', sourdine)} />
      ) : (
        notes.length > 0 && (
          <div className="space-y-2.5">
            {notes.map((n) => (
              <Commentaire key={n.id} note={n} moi={profile?.id ?? null} darkMode={darkMode} />
            ))}
          </div>
        )
      )}

      {equipe ? (
        <div className="relative">
          {candidats.length > 0 && (
            <div
              role="listbox"
              className={cn(
                'absolute bottom-full left-0 mb-1 z-50 w-64 rounded-lg border py-1 shadow-lg',
                darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200',
              )}
            >
              {candidats.map((m, i) => (
                <button
                  key={m.user_id}
                  type="button"
                  role="option"
                  aria-selected={i === mentionIndex}
                  // mousedown : garder le focus dans le champ.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    choisir(m)
                  }}
                  onMouseEnter={() => setMentionIndex(i)}
                  className={cn(
                    'w-full px-3 py-1.5 text-left text-[13px] truncate',
                    i === mentionIndex
                      ? darkMode
                        ? 'bg-slate-800 text-white'
                        : 'bg-slate-100 text-slate-900'
                      : darkMode
                        ? 'text-slate-300'
                        : 'text-slate-700',
                  )}
                >
                  {nomMembre(m)}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={champ}
              value={texte}
              onChange={surSaisie}
              onKeyDown={surTouche}
              rows={1}
              autoFocus
              placeholder={t('inbox.discussion.replyPlaceholder', "Répondre à l'équipe")}
              className={cn(
                'flex-1 resize-none rounded-lg border px-3 py-2 text-[13px] outline-none focus:ring-1 focus:ring-indigo-500',
                darkMode
                  ? 'border-slate-700 bg-slate-800 text-white placeholder-slate-500'
                  : 'border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400',
              )}
            />
            <button
              type="button"
              onClick={envoyer}
              disabled={!texte.trim() || publier.isPending}
              aria-label={t('inbox.discussion.send', "Envoyer à l'équipe")}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
            >
              {publier.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      ) : (
        <p className={cn('text-[12px]', sourdine)}>
          {t('inbox.discussion.teamOnly', 'Commenter est inclus dans Team.')}
        </p>
      )}
    </div>
  )
}

function Commentaire({ note, moi, darkMode }: { note: TimelineEvent; moi: number | null; darkMode: boolean }) {
  const { t } = useTranslation()
  const mien = moi !== null && note.details.author_user_id === moi
  const prenom = note.agent_name.split(' ')[0] || note.agent_name
  return (
    <div className="flex gap-2">
      <span
        className={cn(
          'mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[9.5px] font-semibold',
          mien
            ? 'bg-indigo-600 text-white'
            : darkMode
              ? 'bg-slate-800 text-slate-300'
              : 'bg-slate-200 text-slate-600',
        )}
      >
        {note.agent_initials || '?'}
      </span>
      <div className="min-w-0 flex-1">
        <div className={cn('text-[11px]', darkMode ? 'text-slate-500' : 'text-slate-400')}>
          <span className={cn('font-medium', darkMode ? 'text-slate-300' : 'text-slate-600')}>
            {mien ? t('inbox.discussion.you', 'Vous') : prenom}
          </span>
          {' · '}
          <span className="tabular-nums">{heure(note.timestamp)}</span>
        </div>
        <p
          className={cn(
            'text-[13px] leading-relaxed whitespace-pre-wrap break-words',
            darkMode ? 'text-slate-200' : 'text-slate-700',
          )}
        >
          <TexteAvecMentions
            texte={note.details.content || note.summary}
            noms={(note.details.mentions ?? []).map((m) => m.name)}
            darkMode={darkMode}
          />
        </p>
      </div>
    </div>
  )
}
